import Deal, {DealDTO} from '@/models/Deal';
import LoanApplication from '@/models/LoanApplication';
import Client from '@/models/Client';
import {ReportCalculationType} from '@/commons/enums/ReportCalculationType';
import SettledDocuments, {DocumentStatus} from '@/models/SettledDocuments';
import {Bank} from '@/modules/banks/models/Bank';
import DealsApi from '@/modules/deals/services/DealsApi';
import {clone} from '@/services/utils/BasicUtils';
import {OrganisationId} from '@/models/enums/OrganisationId';
import {ProductType} from '@/commons/enums/ProductType';
import Agent from '@/models/Agent';
import {BankSymbol} from '@/commons/dictionaries/BankSymbol';
import User from '@/models/user/User';
import { InterestRateType } from '@/commons/enums/InterestRateType';

export class ProductVariant {
  calculation!: ReportCalculationType;
  id!: string;
  name!: string;
  value?: number;
  parameterFactor?: number;
  base?: number;
}

export class ReportProduct {
  id!: string;
  name!: string;
}

export class InterestRate {
  interestRateType: InterestRateType | null = null;
  interestRateValue: number = 0;
}

export enum InsurancePeriod {
  MONTH = 'Month',
  QUARTER = 'Quarter',
  YEAR = 'Year',
}

export class InsurancePremiumData {
  amount: Nullable<number> = null;
  period: InsurancePeriod = InsurancePeriod.MONTH;
}

export class InsuranceReportData {
  premium: InsurancePremiumData = new InsurancePremiumData();
}

export default class ApplicationReport extends Deal {
  public settledDocuments!: SettledDocuments;
  public bankApplicationNumber?: Nullable<string> = null;
  public bankContractNumber?: string;
  public settledBankApplicationNumber?: string;
  public settledBankContractNumber?: string;
  public settledApplicationId!: number;
  public settledApplicationNote?: string;
  public dealId?: Nullable<number> = null;
  public applicationId?: number;
  public clients: Client[] = [];
  public clientsIds: Array<number> = [];
  public bank: Bank | null = null;
  public bankSymbol: BankSymbol | null = null;
  public productId: string | null = null;
  public productName: string | null = null;
  public productVariantId: string | null = null;
  public productVariantCalculationStrategy: ReportCalculationType | null = null;
  public productVariantName: string | null = null;
  public amount: number = 0;
  public bankCommission: number = 0;
  public bankMargin: number = 0;
  public consultantCommission: number = 0;
  public duration: number = 0;
  public bankBranchId?: number;
  public bankConsultantEmail?: string;
  public bankConsultant?: string;
  public source: string = '';
  public partnerId: Nullable<number> = null;
  public note: string = '';
  public brokerOrganisationId?: OrganisationId;
  public insurance: InsuranceReportData = new InsuranceReportData();

  public isRodoDocumentsAvailable?: boolean;
  public isOfiDocumentsAvailable?: boolean;
  public isInformationFormDocumentAvailable?: boolean;
  public isDecisionDocumentAvailable?: boolean;
  public isAgreementDocumentAvailable?: boolean;

  public productVariantInstance: ProductVariant | null = null;
  public productInstance: ReportProduct | null = null;
  public interestRate: InterestRate = new InterestRate();
  public lcLifeInsurancePolicyNumber: Array<string> | string = [];

  get product(): ReportProduct | null {
    return this.productInstance;
  }

  get requireBankApplicationNumber(): boolean {
    const requireBankApplicationNumberMortgageOrCash: BankSymbol[] = [
      BankSymbol.MBANK,
      BankSymbol.MILLENNIUM,
      BankSymbol.SANTANDER,
      BankSymbol.BNP,
    ];
    const requireBankApplicationNumberSME = [
      BankSymbol.MBANK,
      BankSymbol.BNP,
      BankSymbol.ING,
      BankSymbol.JANVEST,
      BankSymbol.NEO,
      BankSymbol.BIZ,
      BankSymbol.SBR,
      BankSymbol.WEALTHON_FUND,
      BankSymbol.IDEA_GETIN_LEASING,
      BankSymbol.IFL,
      BankSymbol.FAKTORIA,
      BankSymbol.PRAGMA,
      BankSymbol.SMEO,
      BankSymbol.GRENKE,
      BankSymbol.IMPULS,
      BankSymbol.LEASE_LINK,
      BankSymbol.LEASING_POLSKI,
      BankSymbol.PEAC_FINANCE,
      BankSymbol.PEKAO_SA,
    ];
    if (this.productType === ProductType.SME) {
      return requireBankApplicationNumberSME.includes(this.bankSymbol!);
    } else if (this.productType === ProductType.MORTGAGE || this.productType === ProductType.CASH) {
      return requireBankApplicationNumberMortgageOrCash.includes(this.bankSymbol!);
    } else return false;
  }

  get requireBankContractNumber(): boolean {
    const requireBankContractNumberMortgageOrCash: BankSymbol[] = [
      BankSymbol.ALIOR,
    ];
    const requireBankContractNumberSME = [
      BankSymbol.BOS,
      BankSymbol.FAIR_FINANCE24,
      BankSymbol.INDOS,
      BankSymbol.KRK_PARTNERS,
      BankSymbol.SANTANDER,
      BankSymbol.EFAKTOR,
      BankSymbol.INDOS,
      BankSymbol.COFIDIS,
      BankSymbol.EFL,
      BankSymbol.MILLENNIUM,
      BankSymbol.MLEASING,
      BankSymbol.PKO_BP,
    ];
    if (this.productType === ProductType.SME) {
      return requireBankContractNumberSME.includes(this.bankSymbol!);
    } else if (this.productType === ProductType.MORTGAGE || this.productType === ProductType.CASH) {
      return requireBankContractNumberMortgageOrCash.includes(this.bankSymbol!);
    } else return false;
  }

  set product(newProduct: ReportProduct | null) {
    this.productId = newProduct ? newProduct.id : null;
    this.productName = newProduct ? newProduct.name : null;
    this.productInstance = newProduct;
  }

  get productVariant(): ProductVariant | null {
    return this.productVariantInstance;
  }

  set productVariant(newProductVariant: ProductVariant | null) {
    this.productVariantId = newProductVariant ? newProductVariant.id : null;
    this.productVariantCalculationStrategy = newProductVariant ? newProductVariant.calculation : null;
    this.productVariantName = newProductVariant ? newProductVariant.name : null;
    this.productVariantInstance = newProductVariant;
  }

  constructor(dealDTO?: DealDTO, loanApplication?: LoanApplication) {
    super(dealDTO);
    if (dealDTO) {
      this.dealId = dealDTO.id;
      if (dealDTO.agent) {
        dealDTO.agent = new Agent(dealDTO.agent);
      }
      Object.assign(this, dealDTO);
    }
    if (loanApplication) {
      this.applicationId = loanApplication.id;
      this.productName = loanApplication.name;
      this.bankSymbol = loanApplication.bank!.symbol;
      this.bankConsultantEmail = loanApplication.bankAgentEmail;
      this.bankBranchId = loanApplication.bankBranchId;
      this.amount = loanApplication.amount;
      if (loanApplication.period) {
        this.duration = loanApplication.period;
      }
    }
    if (this.bank) {
      this.bank = new Bank(this.bank);
    }

    if (this.delegatedTo) {
      this.delegatedTo = new User(this.delegatedTo);
    }

    this.clients = this.clients.map(client => new Client(client));
  }

  public get submittedAtObject(): Date | null {
    if (this.submittedAt) {
      return new Date(this.submittedAt);
    }

    return null;
  }

  public set submittedAtObject(newDate: Date | null) {
    if (newDate !== null) {
      this.submittedAt = newDate.toISOString();
    } else {
      this.submittedAt = null;
    }
  }

  public get realisationAtObject(): Date | null {
    if (this.realisationAt) {
      return new Date(this.realisationAt);
    }

    return null;
  }

  public get isCreditReport(): boolean {
    return [ProductType.MORTGAGE, ProductType.CASH, ProductType.SME,].includes(this.productType!);
  }

  public get isInsurance(): boolean {
    return this.productType === ProductType.INSURANCE;
  }

  public set realisationAtObject(newDate: Date | null) {
    if (newDate !== null) {
      this.realisationAt = newDate.toISOString();
    } else {
      this.realisationAt = null;
    }
  }

  async settleReport(deal: Nullable<Deal>, documents?: DocumentStatus): Promise<Deal> {
    const data: PartialDeep<ApplicationReport> = clone(this);

    if (deal) {
      data.clientAgreedCreditBefore21Days = data.clientAgreedCreditBefore21Days === null
        ? deal.clientAgreedCreditBefore21Days : data.clientAgreedCreditBefore21Days;
      data.clientAgreedCreditDraft = data.clientAgreedCreditDraft === null
        ? deal.clientAgreedCreditDraft : data.clientAgreedCreditDraft;
    }

    if (data.partnerId === null) {
      data.partnerId = undefined;
    }
    const areDocumentsAttached = !!(documents &&
      Object.keys(documents).length &&
      Object.values(documents).some(el => !!el)) ||
      Object.values(data.attachments!).some(el => !!el);
    if (data!.clients) {
      data.clientsIds = data.clients.map(c => c!.id!);
      delete data.clients;
    }
    if (!data!.isInsurance) {
      delete data.insurance;
    }
    if (data.interestRate) {
      data.interestRate.interestRateValue = data.interestRate.interestRateType === InterestRateType.FIXED
        ? data.interestRate.interestRateValue : data.bankMargin;
    }
    if (areDocumentsAttached) {
      const formToSend = new FormData();
      formToSend.append('json', JSON.stringify(data));
      for (const documentsKey in documents) {
        const key = documentsKey as keyof DocumentStatus;
        if (documents[key] !== null) {
          formToSend.append(documentsKey, documents[key]!);
        }
      }
      return await DealsApi.settleApplicationWithDocuments(formToSend);
    } else {
      return await DealsApi.settleApplication(data);
    }
  }
}
